import React, { useMemo, useState } from "react";
import { timeOnly } from "@/lib/dates";
import Markdown from "@/components/Markdown";
import LinkPreview from "@/components/LinkPreview";
import Avatar from "@/components/Avatar";
import { MessageSquare, Smile, Image as ImageIcon, FileText, Download, Pencil, Trash2, ArrowRight, Video, Terminal, Bot, Cloud } from "lucide-react";
import { useFileUrl } from "@/lib/fileAccess";
import FeedbackBadge, { CategoryBadge, PriorityBadge, StatusBadge } from "@/components/FeedbackBadge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import EmojiPicker from "@/components/EmojiPicker";
import ConfirmDialog from "@/components/ConfirmDialog";

function timeStr(iso) {
  const d = new Date(iso);
  return timeOnly(d);
}

function Attachment({ file }) {
  // null until the server has minted a token for this file, so nothing is
  // rendered with a credential in its URL and nothing 401s on first paint.
  const url = useFileUrl(file.id);
  const isImg = (file.content_type || "").startsWith("image/");
  if (!url) {
    return (
      <div data-testid={`attachment-${file.id}`}
        className="max-w-md border border-line/10 px-3 py-2 text-[11px] text-fg/40 mono">
        {file.name}
      </div>
    );
  }
  if (isImg) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block max-w-md border border-line/10 overflow-hidden hover:border-line/30 transition-colors" data-testid={`attachment-${file.id}`}>
        <img src={url} alt={file.name} className="w-full max-h-80 object-cover" />
        <div className="px-3 py-1.5 bg-surface flex items-center gap-2 text-[11px] text-fg/60 mono">
          <ImageIcon className="w-3 h-3" /> {file.name}
        </div>
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-surface border border-line/10 px-3 py-2 text-xs hover:border-line/30 transition-colors" data-testid={`attachment-${file.id}`}>
      <FileText className="w-3.5 h-3.5 text-fg/50" />
      <span className="mono truncate max-w-xs">{file.name}</span>
      <Download className="w-3 h-3 text-fg/40" />
    </a>
  );
}

export default function Message({ msg, compact, currentUserId, onReact, onOpenThread, onEdit, onDelete, onOpenFeedback, onJoinCall }) {
  const reactions = Object.entries(msg.reactions || {});
  const urls = useMemo(() => {
    const found = (msg.content || "").match(/https?:\/\/[^\s<>()]+/gi) || [];
    return [...new Set(found.map((u) => u.replace(/[.,;!?]+$/, "")))].slice(0, 2);
  }, [msg.content]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.content || "");
  const isMine = currentUserId === msg.sender_id;
  const isOpsMessage = msg.message_type === "ops_result" || msg.message_type === "claude_response";
  const showHeader = !compact || isOpsMessage;
  const canEdit = isMine && !isOpsMessage && !!onEdit;
  const canDelete = isMine && !!onDelete;

  const saveEdit = async () => {
    const v = draft.trim();
    if (!v || v === msg.content) { setEditing(false); setDraft(msg.content || ""); return; }
    try { await onEdit(v); setEditing(false); }
    catch { /* parent surfaces the error toast */ }
  };

  const cancelEdit = () => { setEditing(false); setDraft(msg.content || ""); };

  const del = () => setConfirmDelete(true);
  const doDelete = async () => {
    setDeleting(true);
    try { await onDelete(); setConfirmDelete(false); }
    catch { /* parent surfaces the error toast */ }
    finally { setDeleting(false); }
  };

  return (
    <div className={`group flex gap-3 fade-in ${compact ? "" : "mt-3"}`} data-testid={`message-${msg.id}`}>
      <div className="w-9 flex-shrink-0">
        {showHeader && (
          msg.atena ? (
            <div
              aria-hidden="true"
              className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-brand/15 border border-brand/40 text-base"
            >🤖</div>
          ) : (
            <Avatar user={{ id: msg.sender_id, name: msg.sender_name, avatar_color: msg.sender_color }} size={36} />
          )
        )}
      </div>
      <div className="flex-1 min-w-0 relative">
        {showHeader && (
          <div className="flex items-baseline gap-2 mb-0.5">
            <span className="font-semibold text-sm">{msg.sender_name}</span>
            {msg.atena && (
              <span
                data-testid="atena-badge"
                title={msg.atena.on_behalf_of ? "Postat de Atena la cererea unui coleg" : "Răspuns generat de Atena"}
                className="label-tiny px-1 py-px border border-brand/40 text-brand uppercase flex-shrink-0"
              >AI</span>
            )}
            <span className="text-[11px] text-fg/40 mono">{timeStr(msg.created_at)}</span>
            {msg.edited_at && <span className="text-[10px] text-fg/30 mono" title="Editat">(editat)</span>}
          </div>
        )}
        {msg.message_type === "ops_result" ? (
          <div className="mt-1 max-w-3xl border border-brand/30 bg-surface-2" data-testid={`ops-result-${msg.id}`}>
            <div className="px-3 py-2 border-b border-line/10 flex items-center gap-2">
              <Terminal className="w-3.5 h-3.5 text-brand" />
              <span className="text-xs font-semibold text-brand">OPS · {msg.ops?.command}</span>
              <span className="ml-auto text-[10px] mono text-fg/40">
                ieșire {msg.ops?.exit_code} · {msg.ops?.duration_ms}ms
              </span>
            </div>
            <pre className="p-3 text-xs mono text-fg/80 whitespace-pre-wrap break-words overflow-x-auto">
              $ {msg.ops?.command}{"\n"}{msg.ops?.output}
            </pre>
            <div className="px-3 py-1.5 border-t border-line/10 text-[9px] uppercase tracking-wider text-fg/25">
              doar în #ops
            </div>
          </div>
        ) : msg.message_type === "claude_response" ? (
          <div className="mt-1 max-w-3xl border border-brand/30 bg-surface-2 p-3" data-testid={`claude-response-${msg.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-4 h-4 text-brand" />
              <span className="text-xs font-semibold text-brand">Claude DevOps</span>
              <span className="ml-auto text-[9px] uppercase tracking-wider text-fg/25">doar în #ops</span>
            </div>
            <div className="text-sm text-fg/90">
              <Markdown>{(msg.content || "").replace(/^🤖 Claude:\s*/, "")}</Markdown>
            </div>
          </div>
        ) : msg.call ? (
          <button
            type="button"
            data-testid={`call-card-${msg.id}`}
            aria-label={`Intră în apel ${msg.call.channel_name || "conversation"}`}
            onClick={() => onJoinCall?.(msg.call.room_name, msg.call.channel_name)}
            className="w-full text-left mt-1 border border-brand/30 bg-surface-2 hover:border-brand/60 transition-colors p-3 max-w-md"
          >
            <div className="flex items-center gap-2 mb-1">
              <Video className="w-4 h-4 text-brand" />
              <span className="text-sm font-semibold text-fg">📹 Apel</span>
            </div>
            <p className="text-sm text-fg/80 break-words">{msg.content}</p>
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-brand hover:text-brand-2">
              Intră <ArrowRight className="w-3 h-3" />
            </span>
          </button>
        ) : msg.kmeet ? (
          <a
            href={msg.kmeet.room_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`kmeet-card-${msg.id}`}
            aria-label={`Deschide kMeet ${msg.kmeet.subject || ""} într-un tab nou`}
            className="block mt-1 border border-brand/30 bg-surface-2 hover:border-brand/60 transition-colors p-3 max-w-md"
          >
            <div className="flex items-center gap-2 mb-1">
              <Cloud className="w-4 h-4 text-brand" />
              <span className="text-sm font-semibold text-fg">☁️ kMeet</span>
              {/* kMeet has no embed SDK, so this leaves the app — say so up front. */}
              <span className="label-tiny text-fg-muted ml-auto">// TAB NOU</span>
            </div>
            <p className="text-sm text-fg/80 break-words">{msg.content}</p>
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-brand hover:text-brand-2">
              Intră <ArrowRight className="w-3 h-3" />
            </span>
          </a>
        ) : msg.deployment ? (
          <div
            data-testid={`deployment-card-${msg.id}`}
            className="mt-1 deploy-card p-3 max-w-md"
          >
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              <FeedbackBadge varName="--deploy-accent" label={msg.deployment.app} />
              <FeedbackBadge
                varName={msg.deployment.env === "production" ? "--deploy-production" : "--deploy-staging"}
                label={msg.deployment.env}
              />
            </div>
            <p className="text-sm font-semibold text-fg break-words">🚀 {msg.deployment.message}</p>
            {msg.deployment.commit && (
              <p className="text-[11px] text-fg/50 mono mt-1">commit: {msg.deployment.commit}</p>
            )}
            {msg.deployment.ticket_id && (
              <button
                type="button"
                data-testid={`deployment-ticket-${msg.deployment.ticket_id}`}
                onClick={() => onOpenFeedback?.(msg.deployment.ticket_id)}
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-brand hover:text-brand-2"
              >
                📋 Deschide în Tracker{msg.deployment.ticket_title ? `: ${msg.deployment.ticket_title}` : ""} <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : msg.feedback ? (
          <div
            data-testid={`feedback-card-${msg.feedback.ticket_id}`}
            className="mt-1 border border-line/10 bg-surface-2 p-3 max-w-md"
          >
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              <CategoryBadge value={msg.feedback.category} />
              <PriorityBadge value={msg.feedback.priority} />
              <StatusBadge value={msg.feedback.status} />
            </div>
            <p className="text-sm font-semibold text-fg break-words">{msg.feedback.title}</p>
            <p className="text-[11px] text-fg/50 mono mt-0.5">{msg.feedback.app}</p>
            <button
              type="button"
              data-testid={`open-tracker-${msg.feedback.ticket_id}`}
              onClick={() => { window.location.href = `/?ticket=${msg.feedback.ticket_id}`; }}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-sm border transition-opacity hover:opacity-80"
              style={{
                color: `rgb(var(--fb-open))`,
                backgroundColor: `rgb(var(--fb-open) / 0.12)`,
                borderColor: `rgb(var(--fb-open) / 0.3)`,
              }}
            >
              📋 Deschide în Tracker <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        ) : editing ? (
          <div className="space-y-1.5">
            <textarea
              data-testid={`edit-input-${msg.id}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
              }}
              autoFocus
              rows={2}
              className="w-full bg-surface-2 border border-line/10 focus:border-line/30 text-fg text-sm p-2 outline-none resize-none"
            />
            <div className="flex items-center gap-2">
              <button data-testid={`edit-save-${msg.id}`} onClick={saveEdit} className="btn-primary text-xs px-2 py-0.5">Salvează</button>
              <button data-testid={`edit-cancel-${msg.id}`} onClick={cancelEdit} className="text-xs text-fg/50 hover:text-fg">Anulează</button>
              <span className="label-tiny opacity-50">Enter = salvează · Esc = anulează</span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-fg/90 leading-relaxed space-y-2">
            <Markdown>{msg.content || ""}</Markdown>
            {compact && msg.edited_at && (
              <span className="text-[10px] text-fg/30 mono" title="Editat">(editat)</span>
            )}
            {msg.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {msg.attachments.map((f) => <Attachment key={f.id} file={f} />)}
              </div>
            )}
          </div>
        )}

        {/* Link previews */}
        {!editing && urls.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {urls.map((u) => <LinkPreview key={u} url={u} />)}
          </div>
        )}

        {/* Reactions */}
        {reactions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {reactions.map(([emoji, uids]) => {
              const mine = uids.includes(currentUserId);
              return (
                <button
                  key={emoji}
                  data-testid={`reaction-${msg.id}-${emoji}`}
                  aria-label={`Reacționează cu ${emoji}; ${uids.length} ${uids.length === 1 ? "reacție" : "reacții"}`}
                  onClick={() => onReact(emoji)}
                  className={`text-xs px-1.5 py-0.5 border transition-colors ${
                    mine
                      ? "bg-brand/15 border-brand/40 text-brand"
                      : "bg-surface border-line/10 text-fg/70 hover:border-line/30"
                  }`}
                >
                  {emoji} <span className="mono">{uids.length}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Thread indicator */}
        {msg.thread_count > 0 && (
          <button
            data-testid={`thread-open-${msg.id}`}
            aria-label={`Deschide firul cu ${msg.thread_count} ${msg.thread_count === 1 ? "reply" : "replies"}`}
            onClick={onOpenThread}
            className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-brand hover:text-brand-2"
          >
            <MessageSquare className="w-3 h-3" /> {msg.thread_count} {msg.thread_count === 1 ? "reply" : "replies"}
          </button>
        )}

        {/* Hover toolbar */}
        {/* Stays mounted while the picker is open: the popover is anchored to
            the trigger inside it, and a display:none trigger has no rect, so
            the picker would fall back to the viewport origin — over the
            sidebar, across the channel list. */}
        <div className={`absolute -top-3 right-0 ${pickerOpen ? "flex" : "hidden group-hover:flex"} items-center gap-0.5 bg-surface border border-line/10 shadow-lg`}>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button data-testid={`react-trigger-${msg.id}`} className="p-1.5 text-fg/50 hover:text-brand" title="Adaugă o reacție" aria-label="Adaugă o reacție">
                <Smile className="w-3.5 h-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="bg-surface border-line/10 p-0 w-auto rounded-none">
              <EmojiPicker onSelect={(e) => { onReact(e); setPickerOpen(false); }} />
            </PopoverContent>
          </Popover>
          {!msg.parent_id && onOpenThread && (
            <button data-testid={`reply-thread-${msg.id}`} onClick={onOpenThread} className="p-1.5 text-fg/50 hover:text-fg" title="Răspunde în fir" aria-label="Răspunde în fir">
              <MessageSquare className="w-3.5 h-3.5" />
            </button>
          )}
          {canEdit && !editing && (
            <button data-testid={`edit-trigger-${msg.id}`} onClick={() => { setDraft(msg.content || ""); setEditing(true); }} className="p-1.5 text-fg/50 hover:text-fg" title="Editează mesajul" aria-label="Editează mesajul">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {canDelete && (
            <button data-testid={`delete-trigger-${msg.id}`} onClick={del} className="p-1.5 text-fg/50 hover:text-red-400" title="Șterge mesajul" aria-label="Șterge mesajul">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {canDelete && (
          <ConfirmDialog
            open={confirmDelete} onOpenChange={setConfirmDelete}
            title="Ștergi mesajul?"
            description="Dispare pentru toată lumea din conversație. Nu se poate anula."
            confirmLabel="Șterge" destructive busy={deleting} onConfirm={doDelete}
            testId={`confirm-delete-${msg.id}`}
          />
        )}
      </div>
    </div>
  );
}
